import React from 'react';

import { TextInput } from '@components/admin/Forms';
import { Layout } from '@components/admin/Layout';
import { ExchangeCalculator } from '@components/admin/widgets/ExchangeCalculator';
import { ExchangeCalendar as ExchangeCalendarComponent } from '@components/admin/widgets/ExchangeCalendar';
import { calendarStrings } from '@data/calendar';
import { useExchangeCalendar } from '@hooks/queries/dashboard/useExchangeCalendar';
import { useSearch } from '@hooks/useSearch';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { iota } from '@utils/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: '為替カレンダー',
    selectDatePrompt: '日付を選択してください。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  title: {
    ...common.textGray800,
    ...common.fontWeight500,
    ...common.flexInline,
    ...common.justifyBetween,
    alignItems: 'flex-end',
    fontSize: 32,
    padding: 12,
  },
  mainContainer: {
    ...common.flexInline,
    columnGap: '1rem',
    padding: 12,
  },
}));

const searchType = t.type({
  date: h.omittable(h.string()),
});

export const ExchangeCalendar: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(searchType);
  const { date: inputDate = new Date().toISOString() } = search;

  const date = dayjs(inputDate);

  const currencyRate = useExchangeCalendar(
    date.startOf('month').subtract(7, 'days').format('YYYY-MM-DD'),
    date.endOf('month').add(7, 'days').format('YYYY-MM-DD'),
  );

  const renderTitle = () => (
    <Formik
      enableReinitialize
      initialValues={{
        year: String(date.get('year')),
        month: String(date.get('month') + 1),
      }}
      validate={(values) => {
        const nextDate = new Date(Number(values.year), Number(values.month) - 1);
        if (!date.isSame(nextDate, 'month')) {
          updateSearch({
            date: nextDate.toISOString(),
          });
        }
      }}
      onSubmit={() => {}}
    >
      <Form className={classes.title}>
        <time dateTime={`${date.year()}-${date.month() + 1}`} data-testid="CalendarDate">
          {calendarStrings.yearMonth(date.toDate())}
        </time>

        <div className={clsx(classes.horizontalSpace1, classes.flexInline)}>
          <TextInput
            name="year"
            choose
            noLabel
            options={iota(new Date().getFullYear() - 2022 + 1, 2022).map((y) => ({
              value: String(y),
              label: String(y),
            }))}
            style={{
              width: 90,
            }}
          />

          <TextInput
            name="month"
            choose
            noLabel
            options={iota(12).map((i) => ({
              value: String(i + 1),
              label: calendarStrings.months(i + 1),
            }))}
            style={{
              width: 90,
            }}
          />
        </div>
      </Form>
    </Formik>
  );

  return (
    <Layout
      title={`${calendarStrings.yearMonth(date.toDate())} - ${strings.title}`}
      loading={currencyRate.isLoading}
      errors={[currencyRate.error]}
    >
      {() => {
        const customItem = currencyRate.data?.find((item) => dayjs(item.date).isSame(date, 'date'));

        const lastItem = currencyRate.data?.[currencyRate.data?.length - 1];

        const finalItem = customItem || lastItem;

        return (
          <div className={classes.container}>
            {renderTitle()}

            <div className={classes.mainContainer}>
              <div style={{ flex: 1 }}>
                <ExchangeCalendarComponent
                  date={date.toDate()}
                  onClick={(d) => updateSearch({ date: d.toISOString() })}
                  items={currencyRate.data!}
                />
              </div>

              <div className={classes.flexColumn} style={{ width: 260, paddingTop: 28 }}>
                {finalItem ? (
                  <ExchangeCalculator date={dayjs(finalItem.date).toDate()} item={finalItem} />
                ) : (
                  <div className={clsx(classes.textGray600, classes.middleText)}>
                    {strings.selectDatePrompt}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }}
    </Layout>
  );
};
